import React, { useCallback } from "react";
import { Handle, Position, useReactFlow } from "react-flow-renderer";
import { useNodeStorage } from "../../../stores/useNodeStorage";

const TrashIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 3V4H4V6H5V20C5 21.1 5.9 22 7 22H17C18.1 22 19 21.1 19 20V6H20V4H15V3H9ZM7 6H17V20H7V6ZM9 8V18H11V8H9ZM13 8V18H15V8H13Z"/>
  </svg>
);

const PLATFORMS = ["Hotmart", "Kiwify", "Eduzz", "Monetizze", "Appmax", "Yampi"];

const EVENTS = {
  Hotmart: [
    { key: "PURCHASE_COMPLETE",   label: "Compra Aprovada",        icon: "✅", color: "#22c55e" },
    { key: "PURCHASE_BILLET_PRINTED", label: "Pix / Boleto Gerado", icon: "💰", color: "#f59e0b" },
    { key: "PURCHASE_ABANDONED",  label: "Abandono de Carrinho",   icon: "🛒", color: "#f97316" },
    { key: "PURCHASE_CANCELED",   label: "Assinatura Cancelada",   icon: "❌", color: "#ef4444" },
    { key: "PURCHASE_REFUNDED",   label: "Reembolso",              icon: "↩️", color: "#8b5cf6" },
    { key: "PURCHASE_REFUSED",    label: "Cartão Recusado",          icon: "💳", color: "#dc2626" },
    { key: "PURCHASE_CHARGEBACK", label: "Chargeback",               icon: "⚠️", color: "#7c3aed" },
  ],
  Kiwify: [
    { key: "order_approved",      label: "Compra Aprovada",        icon: "✅", color: "#22c55e" },
    { key: "abandoned_cart",      label: "Abandono de Carrinho",   icon: "🛒", color: "#f97316" },
    { key: "refund",              label: "Reembolso",              icon: "↩️", color: "#8b5cf6" },
    { key: "subscription_cancel", label: "Assinatura Cancelada",   icon: "❌", color: "#ef4444" },
  ],
  Eduzz: [
    { key: "order_approved", label: "Compra Aprovada",     icon: "✅", color: "#22c55e" },
    { key: "abandoned_cart", label: "Abandono de Carrinho",icon: "🛒", color: "#f97316" },
    { key: "refund",         label: "Reembolso",           icon: "↩️", color: "#8b5cf6" },
  ],
  Monetizze: [
    { key: "order_approved", label: "Compra Aprovada",     icon: "✅", color: "#22c55e" },
    { key: "abandoned_cart", label: "Abandono de Carrinho",icon: "🛒", color: "#f97316" },
    { key: "refund",         label: "Reembolso",           icon: "↩️", color: "#8b5cf6" },
    { key: "sub_cancel",     label: "Assinatura Cancelada",icon: "❌", color: "#ef4444" },
  ],
  Appmax: [
    { key: "order_approved", label: "Compra Aprovada",     icon: "✅", color: "#22c55e" },
    { key: "pix_generated",  label: "Pix Gerado",          icon: "💰", color: "#f59e0b" },
    { key: "abandoned_cart", label: "Abandono de Carrinho",icon: "🛒", color: "#f97316" },
    { key: "refund",         label: "Reembolso",           icon: "↩️", color: "#8b5cf6" },
  ],
  Yampi: [
    { key: "order_approved", label: "Compra Aprovada",     icon: "✅", color: "#22c55e" },
    { key: "boleto",         label: "Boleto Gerado",       icon: "📄", color: "#f59e0b" },
    { key: "abandoned_cart", label: "Abandono de Carrinho",icon: "🛒", color: "#f97316" },
    { key: "refund",         label: "Reembolso",           icon: "↩️", color: "#8b5cf6" },
  ],
};

const PLATFORM_COLORS = {
  Hotmart: { bg: "#e8282b", light: "rgba(232,40,43,0.1)" },
  Kiwify:  { bg: "#00b37e", light: "rgba(0,179,126,0.1)" },
  Eduzz:   { bg: "#f5a623", light: "rgba(245,166,35,0.1)" },
  Monetizze: { bg: "#5cb85c", light: "rgba(92,184,92,0.1)" },
  Appmax:  { bg: "#7b2ff7", light: "rgba(123,47,247,0.1)" },
  Yampi:   { bg: "#0078d4", light: "rgba(0,120,212,0.1)" },
};

export default function WebhookNode({ id, data }) {
  const { setNodes } = useReactFlow();
  const storageItems = useNodeStorage();

  const update = useCallback((patch) => {
    setNodes((nds) =>
      nds.map((n) => n.id === id ? { ...n, data: { ...n.data, ...patch } } : n)
    );
  }, [id, setNodes]);

  const platform = data.platform || "";
  const event = data.event || "";
  const webhookUrl = data.url || (data.flowId ? "https://api.auzaa.com/webhook/flow/" + data.flowId : "");
  const eventsForPlatform = platform ? (EVENTS[platform] || []) : [];
  const selectedEvent = eventsForPlatform.find(e => e.key === event || e.label === event);
  const platformColor = PLATFORM_COLORS[platform] || { bg: "#10b981", light: "rgba(16,185,129,0.1)" };

  return (
    <div style={{
      background: "#ffffff",
      borderRadius: 14,
      border: "1.5px solid rgba(16,185,129,0.20)",
      boxShadow: "0 4px 16px rgba(16,185,129,0.10), 0 1px 4px rgba(0,0,0,0.04)",
      minWidth: 260,
      fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
      overflow: "visible",
      position: "relative",
    }}>
      {/* Delete button */}
      <button
        style={{
          position: "absolute", top: -30, right: 0,
          width: 26, height: 26, borderRadius: "50%",
          background: "#ef4444", border: "none",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", color: "#fff",
        }}
        title="Excluir"
        onClick={() => { storageItems.setNodesStorage(id); storageItems.setAct("delete"); }}
      >
        <TrashIcon />
      </button>

      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "12px 14px 10px",
        borderBottom: "1px solid rgba(16,185,129,0.10)",
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 10,
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, boxShadow: "0 4px 10px rgba(16,185,129,0.28)",
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#10b981", textTransform: "uppercase", letterSpacing: "0.06em" }}>Gatilho</div>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a", marginTop: 1 }}>Webhook</div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "12px 14px 14px", display: "flex", flexDirection: "column", gap: 10 }}>

        {/* Platform selector */}
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Plataforma</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {PLATFORMS.map(p => {
              const pc = PLATFORM_COLORS[p] || { bg: "#64748b", light: "rgba(100,116,139,0.1)" };
              const active = platform === p;
              return (
                <button
                  key={p}
                  onMouseDown={e => e.stopPropagation()}
                  onClick={() => update({ platform: p, event: "" })}
                  style={{
                    padding: "4px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 600,
                    cursor: "pointer", border: "none",
                    background: active ? pc.bg : "#f1f5f9",
                    color: active ? "#fff" : "#64748b",
                    transition: "all 0.15s",
                    boxShadow: active ? `0 2px 8px ${pc.bg}55` : "none",
                  }}
                >{p}</button>
              );
            })}
          </div>
        </div>

        {/* Event selector */}
        {platform && (
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Evento</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              {eventsForPlatform.map(ev => {
                const active = event === ev.key || event === ev.label;
                return (
                  <button
                    key={ev.key}
                    onMouseDown={e => e.stopPropagation()}
                    onClick={() => update({ event: ev.key })}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "8px 12px", borderRadius: 10, fontSize: 12.5, fontWeight: 500,
                      cursor: "pointer",
                      background: active ? `${ev.color}14` : "#f8fafc",
                      border: active ? `1.5px solid ${ev.color}55` : "1.5px solid transparent",
                      color: active ? ev.color : "#374151",
                      textAlign: "left",
                      transition: "all 0.15s",
                    }}
                  >
                    <span style={{ fontSize: 16, lineHeight: 1 }}>{ev.icon}</span>
                    <span>{ev.label}</span>
                    {active && (
                      <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 700, color: ev.color }}>✓ Ativo</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Webhook URL */}
        {webhookUrl && (
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "#f0fdf4", border: "1px solid #bbf7d0",
            borderRadius: 8, padding: "7px 10px",
          }}>
            <span style={{ fontSize: 10, fontFamily: "monospace", color: "#065f46", wordBreak: "break-all", flex: 1, lineHeight: 1.4 }}>
              {webhookUrl}
            </span>
            <button
              style={{
                width: 22, height: 22, borderRadius: 6, border: "1px solid #86efac",
                background: "rgba(16,185,129,0.10)", display: "flex", alignItems: "center",
                justifyContent: "center", cursor: "pointer", color: "#10b981", flexShrink: 0, padding: 0,
              }}
              title="Copiar URL"
              onMouseDown={e => { e.stopPropagation(); navigator.clipboard.writeText(webhookUrl); }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          </div>
        )}

        {/* Product filter */}
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Filtro de Produto (opcional)</div>
          <input
            type="text"
            placeholder="Ex: 422 receitas"
            value={data.productFilter || ""}
            onMouseDown={e => e.stopPropagation()}
            onChange={e => update({ productFilter: e.target.value })}
            style={{
              width: "100%", padding: "7px 10px", borderRadius: 8, fontSize: 12,
              border: "1px solid #e2e8f0", outline: "none", boxSizing: "border-box",
              fontFamily: "Inter, sans-serif", color: "#374151",
              background: data.productFilter ? "#f0fdf4" : "#f8fafc",
              borderColor: data.productFilter ? "#86efac" : "#e2e8f0",
            }}
          />
          {data.productFilter && (
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 4 }}>
              Só dispara se o produto contiver "{data.productFilter}"
            </div>
          )}
        </div>
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        style={{
          right: -8, top: "50%",
          width: 14, height: 14,
          background: "#10b981",
          border: "2px solid #fff",
          boxShadow: "0 2px 8px rgba(16,185,129,0.40)",
        }}
      />
    </div>
  );
}
